import React, { useEffect, useState, useMemo } from "react";
import Pagination from "../../Shared/Pagination/Pagination";
import sortDown from "../../assets/images/sort-down-active 1.svg";
import sortImage from "../../assets/images/sort-icon.svg";
import sortUp from "../../assets/images/sort-up-active 1.svg";
import { CaretDownFill, CaretUpFill } from "react-bootstrap-icons";
import GetSvgIcon from "../../assets/images/svgList";
import { Trash3Fill } from "react-bootstrap-icons";

// add custom table related css
import "./Table.css";

// Define the Column type for the data grid
export type Column = {
  name: any;
  column_name?: any;
  selector?: (row: any, index?: number) => any; // Index is optional here
  cell?: (
    row: any,
    index: number,
    onChange?: (value: any) => void
  ) => JSX.Element;
  enableSorting?: boolean;
  width?: string; // Optional width for columns
};

// Define the props for the DataGridComponent
type DataTableProps = {
  columns: Column[];
  data: any[];
  enableSorting?: boolean;
  enableGlobalSearching?: boolean;
  enableAddRowButton?: boolean;
  handleRowClick?: (row: any) => void;
  onSelectedRowsChange?: (selectedRows: Set<number>) => void;
  CheckboxStatus?: boolean;
  multiselect?: boolean | null;
  isStripped?: boolean;
  customClassName?: string;
  isExpandable?: boolean;
  expandableContentLayout?: (row: any) => JSX.Element;
  enableDeleteRow?: boolean;
  enableIndex?: boolean;
  enableScrollbar?: boolean; // New prop to control scrollbar
  tableHeight?: string; // Height for the table when scrollbar is enabled
  pagination?: {
    recordsPerPage: number;
    totalRecords: number;
    onCurrentPageChange: (page: number) => void;
    currentPage?: number | 1;
    paginationType?: string;
  };
  onSortChange?: (
    sortColumn: Column | null,
    sortDirection: "asc" | "desc"
  ) => void;
  sortingData?: {
    direction: "asc" | "desc";
    column: { column_name: string };
  };
  checkboxDisableCondition?: (row: any) => boolean;
  customDivClassName?:string;
  onDeleteRow?: (index: number) => void;
};

const Table: React.FC<DataTableProps> = ({
  columns,
  data: initialData,
  enableSorting,
  enableGlobalSearching,
  enableAddRowButton,
  isStripped,
  handleRowClick,
  onSelectedRowsChange,
  multiselect = true,
  CheckboxStatus = true,
  customClassName,
  enableDeleteRow = false,
  isExpandable = false,
  expandableContentLayout,
  enableScrollbar = false, // Default is false
  tableHeight = "400px", // Default height
  pagination,
  onSortChange,
  sortingData,
  checkboxDisableCondition,
  customDivClassName,
  onDeleteRow
}) => {
  const [data, setData] = useState(initialData);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set<number>());
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(pagination?.currentPage ?? 1);
  const [expandedRows, setExpandedRows] = useState(new Set<number>());

  useEffect(() => {
    setData(initialData);
    if (onSelectedRowsChange) {
      onSelectedRowsChange(selectedRows);
    }
  }, [selectedRows, onSelectedRowsChange, initialData]);

  // Filter data based on search text for client-side searching
  const filteredData = useMemo(() => {
    if (!enableGlobalSearching || !searchText) return initialData;
    
    const lowerSearchText = searchText.toLowerCase();
    return initialData.filter(row => 
      columns.some(col => {
        const value = col.selector ? col.selector(row) : row[col.name];
        return String(value).toLowerCase().includes(lowerSearchText);
      })
    );
  }, [initialData, searchText, enableGlobalSearching, columns]);

  // Handle client-side pagination
  const paginatedData = useMemo(() => {
    if (!pagination || pagination.paginationType !== "client") return filteredData;
    
    const startIndex = (currentPage - 1) * pagination.recordsPerPage;
    const endIndex = startIndex + pagination.recordsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pagination]);

  // Calculate total records based on pagination type
  const totalRecords = useMemo(() => {
    if (!pagination) return 0;
    return pagination.paginationType === "client" ? filteredData.length : pagination.totalRecords;
  }, [pagination, filteredData]);

  const handleRowCheckboxChange = (index: number) => {
    if (multiselect === null) return; // No selection allowed

    if (!multiselect) {
      // Single select mode
      const newSelectedRows = new Set<number>();
      if (!selectedRows.has(index)) {
        newSelectedRows.add(index);
      }
      setSelectedRows(newSelectedRows);
    } else {
      // Multi select mode
      const newSelectedRows = new Set(selectedRows);
      if (selectedRows.has(index)) {
        newSelectedRows.delete(index);
      } else {
        newSelectedRows.add(index);
      }
      setSelectedRows(newSelectedRows);
      setSelectAll(newSelectedRows.size === data.length);
    }
  };

  const handleSelectAll = () => {
    if (multiselect === null) return; // No selection allowed
    if (!multiselect) return; // No select all in single select mode

    if (selectAll) {
      setSelectedRows(new Set<number>());
    } else {
      const allRows = new Set<number>();
      for (let i = 0; i < data.length; i++) {
        allRows.add(i);
      }
      setSelectedRows(allRows);
    }
    setSelectAll(!selectAll);
  };

  const handleColumnSort = (column: Column) => {
    if (!column.enableSorting) return;

    // For client-side sorting
    if (pagination?.paginationType === "client") {
      const newDirection =
        !sortingData?.column.column_name ||
        sortingData.column.column_name !== column.column_name ||
        sortingData.direction === "desc"
          ? "asc"
          : "desc";

      const sortedData = [...filteredData].sort((a, b) => {
        const aValue = column.selector ? column.selector(a) : a[column.name];
        const bValue = column.selector ? column.selector(b) : b[column.name];
        
        if (aValue < bValue) return newDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return newDirection === "asc" ? 1 : -1;
        return 0;
      });

      setData(sortedData);
      if (onSortChange) {
        onSortChange(column, newDirection);
      }
    } 
    // For server-side sorting
    else if (onSortChange) {
      const newDirection =
        !sortingData?.column.column_name ||
        sortingData.column.column_name !== column.column_name ||
        sortingData.direction === "desc"
          ? "asc"
          : "desc";

      // Reset page to 1 when sorting changes
      if (pagination?.onCurrentPageChange) {
        pagination.onCurrentPageChange(1);
      }

      onSortChange(column, newDirection);
    }
  };

  const handleAddRow = () => {
    const newRow = columns.reduce((acc, column) => {
      acc[column.name] = "";
      return acc;
    }, {} as any);
    setData([...data, newRow]);
  };

  const handleCellChange = (
    rowIndex: number,
    columnName: string,
    value: any
  ) => {
    const updatedData = data.map((row, index) =>
      index === rowIndex ? { ...row, [columnName]: value } : row
    );
    setData(updatedData);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
  };

  const handleDelete = (index: number) => {
    if (onDeleteRow) {
      onDeleteRow(index);
    }
  };

  // Helper function to check if a row is blank
  const isBlankRow = (row: any) => {
    // Check if ItemCode is a CustomDropdown and hasn't been selected
    if (row.ItemCode && React.isValidElement(row.ItemCode)) {
      // You can check if the dropdown has been selected by inspecting the value or another flag
      if (!row.ItemCode.props.selectedItem) {
        return true; // If the dropdown is not selected, treat it as blank
      }
      return false; // If the dropdown has been selected, it's not blank
    }

    // Check if all values in the row are empty or null
    return Object.values(row).every((value) => value === "" || value === null);
  };

  const toggleRowExpansion = (index: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const handlePageChange = (page: number | ((prevState: number) => number)) => {
    if (typeof page === "number") {
      setCurrentPage(page);
      if (pagination?.onCurrentPageChange) {
        pagination.onCurrentPageChange(page);
      }
    } else {
      const newPage = page(currentPage);
      setCurrentPage(newPage);
      if (pagination?.onCurrentPageChange) {
        pagination.onCurrentPageChange(newPage);
      }
    }
  };

  // Determine which data to display based on pagination type
  const displayData = pagination?.paginationType === "client" ? paginatedData : filteredData;

  return (
    <>
      {enableGlobalSearching && (
        <div className="input-group rounded-pill col-auto w-25">
          <input
            type="text"
            placeholder="Search"
            className="form-control rounded-start-pill border border-end-0 pe-0 search-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <span className="input-group-text bg-white border border-start-0 rounded-end-pill cursor-pointer">
            {GetSvgIcon.GlobalSearchIcon}
          </span>
        </div>
      )}
      <div className={`table-container ${customDivClassName?customDivClassName:''}`}>
        <table
          className={`sph-table${
            customClassName === "SuggestedLocation"
              ? " customStriped"
              : " table"
          } ${customClassName} ${isStripped ? "table-striped" : ""}`}
        >
          <thead className={enableScrollbar ? "sticky-header top-0" : ""}>
            <tr>
              {CheckboxStatus && (
                <th scope="col">
                  <input
                    className={`form-check-input sph-checkbox-th ${
                      multiselect === null
                        ? "sph-checkbox-disabled"
                        : multiselect
                        ? "sph-checkbox-enabled"
                        : "sph-checkbox-single"
                    }`}
                    type="checkbox"
                    checked={multiselect ? selectAll : false}
                    onChange={handleSelectAll}
                  />
                </th>
              )}

              {isExpandable && <th scope="col" style={{ width: "50px" }}></th>}
              {columns.map((col, index) => (
                <th
                  className={`${
                    customClassName === "SuggestedLocation" ? `col-3` : ""
                  } ${col.enableSorting ? `cursor-pointer` : ""}`}
                  scope="col"
                  key={index}
                  onClick={() => col.enableSorting && handleColumnSort(col)}
                  style={{
                    ...(col.width ? { width: col.width } : {}),
                    ...(index === 0 && !CheckboxStatus ? { paddingLeft: '30px' } : {})
                  }}
                >
                  {col.name}
                  {col.enableSorting && enableSorting && (
                    <span className="ms-1">
                      {sortingData?.column.column_name === col.column_name ? (
                        sortingData?.direction === "asc" ? (
                          <img src={sortUp} alt="Sort ascending" />
                        ) : (
                          <img src={sortDown} alt="Sort descending" />
                        )
                      ) : (
                        <img
                          src={sortImage}
                          alt="Sort"
                          style={{ opacity: 0.5 }}
                        />
                      )}
                    </span>
                  )}
                </th>
              ))}
              {enableDeleteRow && <th scope="col"></th>}
            </tr>
          </thead>

          <tbody className="table-body">
            {displayData.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (CheckboxStatus ? 1 : 0) +
                    (enableDeleteRow ? 1 : 0) +
                    (isExpandable ? 1 : 0)
                  }
                  className="text-center"
                >
                  No records found
                </td>
              </tr>
            ) : (
              displayData.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <tr
                    key={rowIndex}
                    className={`${
                      customClassName === "SuggestedLocation" ? "col-3" : ""
                    }`}
                    onClick={(e) => {
                      if (
                        !(e.target as HTMLElement).closest(".form-check-input")
                      ) {
                        handleRowClick && handleRowClick(row);
                      }
                    }}
                  >
                    {CheckboxStatus && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          className={`form-check-input sph-checkbox ${
                            multiselect === null
                              ? "sph-checkbox-disabled"
                              : "sph-checkbox-enabled"
                          }`}
                          type="checkbox"
                          checked={selectedRows.has(rowIndex)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleRowCheckboxChange(rowIndex);
                          }}
                          disabled={
                            checkboxDisableCondition
                              ? checkboxDisableCondition(row)
                              : false
                          }
                        />
                      </td>
                    )}
                    {isExpandable && (
                      <td>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(rowIndex);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {expandedRows.has(rowIndex) ? (
                            <CaretUpFill />
                          ) : (
                            <CaretDownFill />
                          )}
                        </span>
                      </td>
                    )}

                    {columns.map((col, colIndex) => (
                      <td
                        className={`${
                          customClassName === "SuggestedLocation" ? `col-3` : ""
                        }`}
                        key={colIndex}
                        style={colIndex === 0 && !CheckboxStatus ? { paddingLeft: '30px' } : {}}
                      >
                        {col.cell
                          ? col.cell(row, rowIndex, (value) =>
                              handleCellChange(rowIndex, col.name, value)
                            )
                          : col.selector
                          ? col.selector(row, rowIndex)
                          : null}
                      </td>
                    ))}
                    {enableDeleteRow && (
                      <td >
                        {!isBlankRow(row) && (
                          <Trash3Fill
                          className="cursor-pointer mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(rowIndex);
                            }}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpandable && expandedRows.has(rowIndex) && (
                    <tr className="">
                      <td 
                        className="bg-transparent mb-0" 
                        colSpan={
                          columns.length +
                          (CheckboxStatus ? 1 : 0) +
                          (enableDeleteRow ? 1 : 0) +
                          (isExpandable ? 1 : 0)
                        }
                      >
                        {expandableContentLayout && expandableContentLayout(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination !== undefined &&
        totalRecords > 0 && (
          <Pagination
            totalRecords={totalRecords}
            recordsPerPage={pagination.recordsPerPage}
            currentPageState={[currentPage, handlePageChange]}
          />
        )}
      {enableAddRowButton && <button onClick={handleAddRow}>Add Row</button>}
    </>
  );
};

export default Table;